/**
 * Canonical (and ONLY — Silent NACK / single-producer fitness) producer for
 * `POST /api/track/impressions`. Per-tab singleton buffer: callers report
 * card exposures via {@link recordImpression}; the buffer throttles,
 * batches and flushes them as beacons.
 *
 * Behaviour (approved O6/O7 defaults — decisions live in the PURE core,
 * `@kraahl/contract` impression-buffer-core; this shell owns state, timers, listeners and
 * transport):
 *   - Throttle: same listingId not re-buffered within 60s per tab session
 *     (anti scroll-jitter, NOT semantic dedup).
 *   - Flush when: buffer reaches 20 events | 10s idle since the last record
 *     | `visibilitychange` -> hidden (PRIMARY leave signal — mobile browsers
 *     kill tabs without firing pagehide) | `pagehide` (fallback).
 *   - Flush = one `navigator.sendBeacon` per ≤50-event batch (contract cap;
 *     larger backlogs slice into multiple beacons), `fetch keepalive`
 *     fallback — same transport shape as `trackListingView`. Body is
 *     Zod-parsed through `TrackImpressionsRequestSchema` before send;
 *     `guestId` read from `getGuestId()` at flush time.
 *   - SSR-safe: no window/document access at import time — listeners are
 *     registered lazily on the first record. Fire-and-forget: nothing here
 *     ever throws into the UI.
 *
 * @see packages/contract/src/track-impressions.ts — wire schema
 * @see packages/contract/src/impression-buffer-core.ts — pure decision core (shared with mobile)
 * @see hooks/use-impression-observer.ts + marketplace-vertical-snap-feed.tsx — callers
 */

import {
  IMPRESSION_IDLE_FLUSH_MS,
  normalizeImpressionPosition,
  shouldBufferImpression,
  shouldFlushAtCount,
  sliceIntoBatches,
  TrackImpressionsRequestSchema,
  type ImpressionEvent,
  type ImpressionSurface,
  type TrackImpressionsRequest,
} from "@kraahl/contract";
import { getGuestId } from "@/lib/guest";

export interface RecordImpressionIntent {
  listingId: string;
  surface: ImpressionSurface;
  /** Card index at impression time; invalid/negative values are dropped. */
  position?: number | null;
}

const TRACK_IMPRESSIONS_ENDPOINT = "/api/track/impressions";

// ---------------------------------------------------------------------------
// Per-tab singleton state (module scope). SSR-safe: plain data only — no
// window/document reads until the first record.
// ---------------------------------------------------------------------------

let pending: ImpressionEvent[] = [];
const lastBufferedAtMs = new Map<string, number>();
let idleTimer: ReturnType<typeof setTimeout> | null = null;
let listenersRegistered = false;

function sendBatch(body: TrackImpressionsRequest): void {
  const parsed = TrackImpressionsRequestSchema.safeParse(body);
  if (!parsed.success) return; // malformed batch — drop, never throw

  const json = JSON.stringify(parsed.data);

  if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
    const blob = new Blob([json], { type: "application/json" });
    const queued = navigator.sendBeacon(TRACK_IMPRESSIONS_ENDPOINT, blob);
    if (queued) return;
    // sendBeacon can refuse (quota / CSP / >64KB) — fall through to fetch.
  }

  if (typeof fetch === "function") {
    void fetch(TRACK_IMPRESSIONS_ENDPOINT, {
      method: "POST",
      keepalive: true,
      headers: { "Content-Type": "application/json" },
      body: json,
    }).catch(() => {
      // Fire-and-forget — a dropped impressions beacon is not a user-facing error.
    });
  }
}

/**
 * Drain the buffer and send everything now, in ≤50-event beacons. Called by
 * the flush triggers below; exported for the leave-signal handlers and for
 * tests (the real producer must be exercisable without faking timers).
 */
export function flushImpressionsNow(): void {
  try {
    if (idleTimer !== null) {
      clearTimeout(idleTimer);
      idleTimer = null;
    }
    if (pending.length === 0) return;

    const drained = pending;
    pending = [];

    const guestId = getGuestId() || null;
    for (const batch of sliceIntoBatches(drained)) {
      sendBatch({ events: batch, ...(guestId ? { guestId } : {}) });
    }
  } catch {
    // Tracking must never throw into the UI.
  }
}

function registerLeaveListenersOnce(): void {
  if (listenersRegistered) return;
  if (typeof document === "undefined" || typeof window === "undefined") return;
  listenersRegistered = true;

  // PRIMARY leave signal: mobile browsers routinely kill background tabs
  // without firing pagehide/unload; visibilitychange->hidden is the last
  // reliable moment to hand the backlog to sendBeacon.
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flushImpressionsNow();
  });
  // Fallback for UAs/navigations where visibilitychange is skipped.
  window.addEventListener("pagehide", () => flushImpressionsNow());
}

function armIdleFlush(): void {
  if (idleTimer !== null) clearTimeout(idleTimer);
  // Reset-on-record timer implementing the pure `shouldIdleFlush` threshold:
  // fires IMPRESSION_IDLE_FLUSH_MS after the LAST record.
  idleTimer = setTimeout(() => {
    idleTimer = null;
    flushImpressionsNow();
  }, IMPRESSION_IDLE_FLUSH_MS);
}

/**
 * Report one card exposure. Throttled per listingId (60s/tab), buffered,
 * flushed in batches — see file header. Safe to call from render effects;
 * never throws.
 */
export function recordImpression(intent: RecordImpressionIntent): void {
  try {
    if (!intent.listingId) return;

    const now = Date.now();
    if (!shouldBufferImpression(lastBufferedAtMs, intent.listingId, now)) return;
    lastBufferedAtMs.set(intent.listingId, now);

    const position = normalizeImpressionPosition(intent.position);
    pending.push({
      listingId: intent.listingId,
      surface: intent.surface,
      ...(position !== undefined ? { position } : {}),
    });

    registerLeaveListenersOnce();

    if (shouldFlushAtCount(pending.length)) {
      flushImpressionsNow();
    } else {
      armIdleFlush();
    }
  } catch {
    // Tracking must never throw into the UI.
  }
}

/**
 * TEST-ONLY: reset the per-tab singleton between test cases. Not for
 * application code — the whole point of the buffer is that it persists for
 * the tab session.
 */
export function __resetImpressionBufferForTests(): void {
  pending = [];
  lastBufferedAtMs.clear();
  if (idleTimer !== null) {
    clearTimeout(idleTimer);
    idleTimer = null;
  }
  // listenersRegistered intentionally NOT reset: jsdom document persists per
  // test file; re-registering would stack duplicate handlers.
}
